import { Component } from '@theme/component';

/**
 * A custom element that control tabs section.
 *
 * @typedef {object} Refs
 * @property {HTMLLinkElement[]} tabControls - The tab buttons.
 * @property {HTMLElement} tabControlsWrapper - The tab buttons wrapper.
 * @property {HTMLElement[]} tabs - The tab content.
 *
 * @extends Component<Refs>
 */
class TabsControls extends Component {
  requiredRefs = ['tabs', 'tabControls', 'tabControlsWrapper'];

  /** @type {number} */
  activeTabIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.openFirstTab();
    this.refs.tabControlsWrapper.addEventListener('click', this.changeTab.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.tabControlsWrapper.removeEventListener('click', this.changeTab.bind(this));
  }

  openFirstTab() {
    const firstTab = this.refs.tabs[this.activeTabIndex];
    const firstTabControl = this.refs.tabControls[this.activeTabIndex];
    if (!firstTab || !firstTabControl) return;

    firstTab.classList.remove('hidden');
    firstTabControl.classList.add('tab--active');
  }

  /**
   * Change tab.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  changeTab(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    /** @type {HTMLLinkElement | null} */
    const clickedTab = event.target.closest('.tab-control');
    if (!clickedTab) return;

    const activeTabIndex = this.refs.tabControls.indexOf(clickedTab);
    if (activeTabIndex === -1) return;

    if (activeTabIndex == this.activeTabIndex) return;

    const { tabs, tabControls } = this.refs;
    if (!tabs[activeTabIndex] || !tabControls[activeTabIndex]) return;

    tabs.forEach((content, index) => {
      const isActive = index === activeTabIndex;
      content.classList.toggle('hidden', !isActive);
      content.setAttribute('aria-hidden', `${!isActive}`);
    });

    tabControls.forEach((tab, index) => {
      const isActive = index === activeTabIndex;
      tab.classList.toggle('tab--active', isActive);

      tab.setAttribute('aria-selected', `${isActive}`);
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    tabControls[activeTabIndex].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });

    this.activeTabIndex = activeTabIndex;
  }
}

if (!customElements.get('tab-controls')) {
  customElements.define('tab-controls', TabsControls);
}
